import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, LogOut, LayoutDashboard, Users, ClipboardList, MailPlus, Settings as SettingsIcon } from "lucide-react";
import { api, publicApi, tokenStore } from "./api";

const FIELD = "w-full rounded-2xl bg-white border border-border px-5 py-3.5 text-charcoal outline-none focus:ring-2 focus:ring-coral focus:border-transparent";

function useAdminPrivateMeta() {
  useEffect(() => {
    // robots -> noindex,nofollow
    let robots = document.querySelector('meta[name="robots"]');
    let robotsCreated = false;
    if (!robots) { robots = document.createElement("meta"); robots.name = "robots"; document.head.appendChild(robots); robotsCreated = true; }
    const prevRobots = robots.getAttribute("content");
    robots.setAttribute("content", "noindex, nofollow");

    // title
    const prevTitle = document.title;
    document.title = "Partner Login | Community Experiences by Kay";

    // canonical — do not reuse the public homepage canonical on admin
    const canonical = document.querySelector('link[rel="canonical"]');
    let prevCanonicalHref = null;
    if (canonical) {
      prevCanonicalHref = canonical.getAttribute("href");
      canonical.remove();
    }

    return () => {
      if (robotsCreated) document.head.removeChild(robots);
      else robots.setAttribute("content", prevRobots || "index, follow");
      document.title = prevTitle;
      if (prevCanonicalHref) {
        const link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        link.setAttribute("href", prevCanonicalHref);
        document.head.appendChild(link);
      }
    };
  }, []);
}

function LoginView({ onLogin }) {
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await publicApi().post("/auth/login", creds);
      tokenStore.set(data.token);
      toast.success("Welcome back, Kay.");
      onLogin();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid email or password.");
    } finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen bg-cream grid place-items-center px-6">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-md rounded-[2rem] bg-white border border-border p-8 md:p-10">
        <div className="h-14 w-14 rounded-2xl bg-coral/12 grid place-items-center mb-6"><Lock size={26} className="text-coral" /></div>
        <h1 className="font-poppins font-bold text-3xl text-charcoal">Partner Login</h1>
        <p className="mt-2 text-charcoal/60">Community Experiences by Kay CRM</p>
        <div className="mt-7 space-y-4">
          <input data-testid="admin-email" type="email" autoComplete="email" placeholder="Email" className={FIELD}
            value={creds.email} onChange={(e) => setCreds({ ...creds, email: e.target.value })} />
          <input data-testid="admin-password" type="password" autoComplete="current-password" placeholder="Password" className={FIELD}
            value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} />
        </div>
        <button data-testid="admin-login-btn" type="submit" disabled={loading}
          className="mt-7 w-full rounded-full bg-coral text-cream font-semibold text-lg py-4 transition-all hover:-translate-y-0.5 disabled:opacity-60">
          {loading ? "Signing in..." : "Sign In"}
        </button>
        <a href="/" className="mt-5 block text-center text-charcoal/60 font-semibold hover:text-charcoal">← Back to site</a>
      </form>
    </div>
  );
}

const NAV = [
  { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/admin/leads", label: "Leads", icon: Users, testid: "nav-leads" },
  { to: "/admin/tasks", label: "Tasks", icon: ClipboardList, testid: "nav-tasks" },
  { to: "/admin/templates", label: "Templates", icon: MailPlus, testid: "nav-templates" },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

export default function AdminApp() {
  useAdminPrivateMeta();
  const [authed, setAuthed] = useState(!!tokenStore.get());
  const nav = useNavigate();
  const logout = () => { tokenStore.clear(); setAuthed(false); nav("/admin"); };

  if (!authed) return <LoginView onLogin={() => setAuthed(true)} />;

  return (
    <div className="min-h-screen bg-cream">
      <header className="bg-plum text-cream">
        <div className="max-w-[1400px] mx-auto px-6 py-5 flex items-center justify-between gap-4">
          <div>
            <p className="text-cream/60 text-xs uppercase tracking-wider">Community Experiences by Kay</p>
            <h1 className="font-poppins font-bold text-xl">Phase 1 CRM</h1>
          </div>
          <button data-testid="admin-logout-btn" onClick={logout} className="inline-flex items-center gap-2 rounded-full bg-coral px-4 py-2.5 font-semibold text-sm">
            <LogOut size={15} /> Log out
          </button>
        </div>
        <nav className="max-w-[1400px] mx-auto px-6 pb-3 flex gap-1 overflow-x-auto">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={n.testid}
              className={({ isActive }) => `inline-flex items-center gap-2 rounded-full px-4 py-2 font-semibold text-sm whitespace-nowrap ${isActive ? "bg-cream text-plum" : "text-cream/80 hover:bg-cream/10"}`}>
              <n.icon size={15} /> {n.label}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="max-w-[1400px] mx-auto px-6 py-8">
        <Outlet />
      </main>
    </div>
  );
}

export function AdminIndex() { return <Navigate to="/admin/dashboard" replace />; }
